// /////////////////////////////////////////////////////////////////////
//
//
// (C) Copyright 2021 Autodesk, Inc. All rights reserved.
//
//                     ****  CONFIDENTIAL MATERIAL  ****
//
// The information contained herein is confidential, proprietary to
// Autodesk, Inc., and considered a trade secret.  Use of this information
// by anyone other than authorized employees of Autodesk, Inc. is granted
// only under a written nondisclosure agreement, expressly prescribing the
// the scope and manner of such use.
//
// /////////////////////////////////////////////////////////////////////

import {
  CompositeExtractType,
  CompositeRenamingType,
  CreateJobRequest,
  DeckardJobOptions,
  DirectoryDto,
  DirectorySelectionOptions,
  EditJobRequest,
  EditScheduleRequest,
  EmailPreferences,
  ExtractLocationType,
  FileDestinationNamingType,
  FileDto,
  Job,
  JobDirectory,
  JobFile,
  JobScheduleType,
  ProjectWiseDirectory,
  ProjectWiseFile,
  Schedule,
  StorageType
} from '../../clients/Classes';
import {Task} from '../Task';
import {FileStructureTranslator} from './FileStructureTranslator';
import {CronTranslator} from './CronTranslator';
import {ZipTaskSetting} from '../ZipTaskSetting';
import {ZipEntryUI} from '../ZipEntryUI';
import {FileUI} from "../FileUI";
import {GetUnique, IncludeZip, NameOf} from "../../Utility";
import {DirectoryUI} from "../DirectoryUI";

export class TaskTranslator {
  static GetTask(job: Job): Task {
    const task = new Task();
    task.Id = job.id;
    task.UserId = job.userId;
    task.CustomerId = job.customerId;
    task.BulkTaskId = job.bulkJobId;
    task.Name = job.name;
    task.HubId = job.hubId;
    task.ProjectId = job.projectId;

    let hubName: string | undefined;
    let projectName: string | undefined;

    job.models!
      .filter(m => job.options?.singleFiles == null || job.options.singleFiles[m.id!] == null)
      .forEach(apiModel => {
        task.Files.push(FileStructureTranslator.GetFile(apiModel));
        if (apiModel instanceof ProjectWiseFile) {
          hubName = 'ProjectWise';
          projectName = apiModel.projectName;
        } else if (apiModel instanceof JobFile) {
          hubName = apiModel.hubName;
          projectName = apiModel.projectName;
        }
      });
    job.directories!.forEach(apiDirectory => {
      task.Directories.push(FileStructureTranslator.GetDirectory(apiDirectory, false));
      if (apiDirectory instanceof ProjectWiseDirectory) {
        hubName = 'ProjectWise';
        projectName = apiDirectory.projectName;
      } else if (apiDirectory instanceof JobDirectory) {
        hubName = apiDirectory.hubName;
        projectName = apiDirectory.projectName;
      }
    });
    task.RawStatus = job.status;
    task.IsPaused = job.schedule != null && job.schedule.isPaused!;
    task.EmailOnCompletion = job.options!.emailPreferences?.emailOnCompletion === true;
    task.AttachCsv = job.options!.emailPreferences?.attachExportFiles === true;
    task.DoNotMaintainFolderStructure = job.options!.doNotMaintainFolderStructure === true;
    task.Archive = job.options!.archive !== false;
    task.HubName = hubName;
    task.ProjectName = projectName;
    switch (job.scheduleType) {
      case JobScheduleType.None:
        break;
      case JobScheduleType.OnceNow:
        task.Trigger = 'OnceNow';
        break;
      case JobScheduleType.OnceLater:
        task.Trigger = 'OnceLater';
        task.StartDate = job.nextRun == null || job.nextRun!.startTime! < new Date()
          ? new Date(new Date().getTime() + (15 * 60 * 1000))
          : job.nextRun.startTime;
        break;
      case JobScheduleType.Recurring:
        task.Trigger = 'Recurring';
        task.RecurrenceSettings = CronTranslator.GetSettings(job.schedule!);
        task.StartDate = job.schedule!.startDate;
        break;
      case JobScheduleType.OnPublish:
        task.Trigger = 'OnPublish';
        break;
    }

    if (job.options == null) {
      task.ExportLocationType = ExtractLocationType.ModelDirectory;
      task.ExportDestinationNaming = FileDestinationNamingType.AppendTimestamp;
      task.RenameType = CompositeRenamingType.RenameExisting;
    } else {
      task.ExportLocationType = job.options.locationType ?? ExtractLocationType.None;
      task.ExportDirectory = job.options.extractDirectory == null
        ? undefined
        : FileStructureTranslator.GetDirectory(job.options.extractDirectory, false);
      task.ExportDestinationNaming = job.options.destinationNamingType ?? FileDestinationNamingType.None;
      task.CompositeExtractType = job.options.compositeExtractType ?? CompositeExtractType.ReSaveCloudModel;
      task.RenameType = job.options.compositeRenamingType ?? CompositeRenamingType.RenameExisting;
      if (job.options.singleFiles != null) {
        for (const key of Object.keys(job.options.singleFiles)) {
          const apiFile = job.models!.find(m => m.id === key);

          if (apiFile == null) {
            console.error(`Failed to find model with id ${key}`);
            continue;
          }
          const file = FileStructureTranslator.GetFile(apiFile);
          const setting = new ZipTaskSetting(file);

          const zips = job.options.singleFiles[key];
          zips.forEach(z => {
            const zip = new ZipEntryUI(apiFile.id!, z, apiFile.projectId!);
            zip.HubId = apiFile instanceof JobFile ? apiFile.hubId : undefined;
            zip.ParentId = apiFile.id;
            setting.Zips.push(zip);
          });

          task.Files.push(setting);
        }
      }

      if (job.nonDynamicDirectories != null) {
        job.nonDynamicDirectories.forEach(apiDirectory => {
          const directory = FileStructureTranslator.GetDirectory(apiDirectory, false);
          directory.IsDynamic = false;
          directory.IncludeThisFolder = true;
          task.Directories.push(directory);
        });
      }

      if (job.options.directorySelectionOptions != null) {
        for (const key of Object.keys(job.options.directorySelectionOptions)) {
          const directory = task.Directories.find(d => d.Id === key);

          if (directory == null) {
            console.warn(`Could not find directory with id ${key}`);
            continue;
          }

          const options = job.options.directorySelectionOptions[key];
          directory.IsDynamic = true;
          directory.IsRecursive = options?.recursive === true;
          directory.IncludeThisFolder = options?.copyCurrentFolder === true;
          directory.FileTypeFilter = options?.fileTypeFilters;
        }
      }
    }

    task.LastRun = job.lastRun?.startTime;
    task.NextRun = job.nextRun?.startTime;

    task.LastRunOrCreated = job.lastRunOrCreatedTime;
    task.Description = job.description;

    return task;
  }

  static GetCreateObject(task: Task): CreateJobRequest {
    let scheduleType: JobScheduleType = JobScheduleType.None;

    switch (task.Trigger) {
      case 'OnceNow':
        scheduleType = JobScheduleType.OnceNow;
        break;
      case 'OnceLater':
        scheduleType = JobScheduleType.OnceLater;
        break;
      case 'Recurring':
        scheduleType = JobScheduleType.Recurring;
        break;
      case 'OnPublish':
        scheduleType = JobScheduleType.OnPublish;
        break;
    }

    const directoryOptions: { [key: string]: DirectorySelectionOptions } = {};
    if (task.Directories != null) {
      task.Directories
        .filter(d => d.IsDynamic)
        .forEach(d => directoryOptions[d.Id] = new DirectorySelectionOptions({
          recursive: d.IsRecursive,
          copyCurrentFolder: d.IncludeThisFolder,
          fileTypeFilters: d.FileTypeFilter == null || d.FileTypeFilter.length === 0 ? undefined : d.FileTypeFilter
        }));
    }

    const options = new DeckardJobOptions({
      locationType: task.ExportLocationType,
      destinationNamingType: task.ExportDestinationNaming,
      extractDirectory: task.ExportDirectory == null
        ? undefined :
        FileStructureTranslator.GetApiDirectoryDto(task.ExportDirectory),
      emailPreferences: new EmailPreferences({
        emailOnCompletion: task.EmailOnCompletion,
        attachExportFiles: task.AttachCsv
      }),
      singleFiles: this.GetSingleFiles(task),
      doNotMaintainFolderStructure: task.DoNotMaintainFolderStructure,
      archive: task.Archive,
      directorySelectionOptions: directoryOptions,
      compositeExtractType: task.CompositeExtractType,
      compositeRenamingType: task.RenameType
    });

    const projectWiseFiles = task.Files
      .filter(file => file instanceof FileUI && file.StorageType === StorageType.ProjectWise)
      .map(model => FileStructureTranslator.GetApiFileDto(model as FileUI));

    const allModels = task.Files
      .filter(model => model instanceof FileUI && model.StorageType === StorageType.Forge)
      .map(model => FileStructureTranslator.GetApiFileDto(model as FileUI));

    // Add in the models from the zips
    task.Files
      .filter(m => m instanceof ZipTaskSetting)
      .forEach(z => {
          const zip = z as ZipTaskSetting;
          const existingModel = allModels.find(m => m.id === zip.File.Id);
          if (existingModel == null) {
            allModels.push(FileStructureTranslator.GetApiFileDto(zip.File));
          }
        }
      );

    return new CreateJobRequest({
      name: task.Name,
      models: [...allModels, ...projectWiseFiles],
      directories: task.Directories
        .filter(d => d.IsDynamic)
        .map(d => FileStructureTranslator.GetApiDirectoryDto(d)),
      schedule: this.GetScheduleFromTask(task),
      options: options,
      jobScheduleType: scheduleType,
      runOnceDateTime: task.Trigger === 'OnceLater' ? task.StartDate : undefined,
      nonDynamicDirectories: task.Directories
        .filter(d => !d.IsDynamic)
        .map(d => FileStructureTranslator.GetApiDirectoryDto(d)),
      description: task.Description
    });
  }

  static GetTaskUpdate(original: Task, updated: Task): EditJobRequest {
    const scheduleNew = this.GetScheduleFromTask(updated);

    let scheduleEdit: EditScheduleRequest | undefined = undefined;

    let scheduleType: JobScheduleType = JobScheduleType.None;

    switch (updated.Trigger) {
      case 'OnceNow':
        scheduleType = JobScheduleType.OnceNow;
        break;
      case 'OnceLater':
        scheduleType = JobScheduleType.OnceLater;
        break;
      case 'Recurring':
        scheduleType = JobScheduleType.Recurring;
        break;
      case 'OnPublish':
        scheduleType = JobScheduleType.OnPublish;
        break;
    }

    if (scheduleNew != null) {
      scheduleEdit = new EditScheduleRequest({
        cronString: updated.RecurrenceSettings == null ? undefined : CronTranslator.GetCronString(updated.RecurrenceSettings),
        startDate: scheduleNew.startDate,
        endDate: scheduleNew.endDate,
        clearEndDate: false,
        clearStartDate: false,
        maxRuns: scheduleNew.maxRuns
      });
    }

    let options: DeckardJobOptions | undefined;
    const hasContentUpdates = this.HasUpdatedDirectories(original, updated)
      || this.HasUpdatedFiles(original, updated);

    if (original.EmailOnCompletion !== updated.EmailOnCompletion
      || original.ExportLocationType !== updated.ExportLocationType
      || original.ExportDestinationNaming !== updated.ExportDestinationNaming
      || original.ExportDirectory?.Id !== updated.ExportDirectory?.Id
      || original.AttachCsv !== updated.AttachCsv
      || original.CompositeExtractType !== updated.CompositeExtractType
      || original.RenameType !== updated.RenameType
      || hasContentUpdates) {
      const directoryOptions: { [key: string]: DirectorySelectionOptions } = {};
      if (updated.Directories != null) {
        updated.Directories
          .filter(d => d.IsDynamic)
          .forEach(d => directoryOptions[d.Id] = new DirectorySelectionOptions({
            recursive: d.IsRecursive,
            copyCurrentFolder: d.IncludeThisFolder,
            fileTypeFilters: d.FileTypeFilter != null && d.FileTypeFilter.length > 0 ? d.FileTypeFilter : undefined
          }));
      }

      options = new DeckardJobOptions({
        locationType: updated.ExportLocationType,
        destinationNamingType: updated.ExportDestinationNaming,
        extractDirectory: updated.ExportDirectory == null
          ? undefined
          : FileStructureTranslator.GetApiDirectoryDto(updated.ExportDirectory),
        emailPreferences: new EmailPreferences({
          emailOnCompletion: updated.EmailOnCompletion,
          attachExportFiles: updated.AttachCsv
        }),
        singleFiles: this.GetSingleFiles(updated),
        doNotMaintainFolderStructure: updated.DoNotMaintainFolderStructure,
        archive: updated.Archive,
        directorySelectionOptions: directoryOptions,
        compositeExtractType: updated.CompositeExtractType,
        compositeRenamingType: updated.RenameType
      });
    }

    const originalZips = original.Files
      .filter(f => f instanceof ZipTaskSetting) as ZipTaskSetting[];
    const originalFiles = original.Files
      .filter(f => f instanceof FileUI)
      .concat(originalZips.map(z => z.File)) as FileUI[];

    const updatedZips = updated.Files
      .filter(f => f instanceof ZipTaskSetting) as ZipTaskSetting[];
    const updatedFiles = updated.Files
      .filter(f => f instanceof FileUI)
      .concat(updatedZips.map(z => z.File)) as FileUI[];

    const addFiles: FileDto[] = GetUnique<FileUI>(originalFiles, updatedFiles, NameOf<FileUI>('Id'))
      .map(f => FileStructureTranslator.GetApiFileDto(f));
    const removeFiles: FileDto[] = GetUnique<FileUI>(updatedFiles, originalFiles, NameOf<FileUI>('Id'))
      .map(f => FileStructureTranslator.GetApiFileDto(f));

    const directoriesOriginalDynamic = original.Directories
      .filter(d => d.IsDynamic);
    const directoriesOriginalNonDynamic = original.Directories
      .filter(d => !d.IsDynamic);
    const directoriesUpdatedDynamic = updated.Directories
      .filter(d => d.IsDynamic);
    const directoriesUpdatedNonDynamic = updated.Directories
      .filter(d => !d.IsDynamic);

    const addDirectories: DirectoryDto[] =
      GetUnique<DirectoryUI>(directoriesOriginalDynamic, directoriesUpdatedDynamic, NameOf<DirectoryUI>('Id'))
        .map(d => FileStructureTranslator.GetApiDirectoryDto(d));
    const removeDirectories: DirectoryDto[] =
      GetUnique<DirectoryUI>(directoriesUpdatedDynamic, directoriesOriginalDynamic, NameOf<DirectoryUI>('Id'))
        .map(d => FileStructureTranslator.GetApiDirectoryDto(d));

    const addNonDynamic: DirectoryDto[] =
      GetUnique<DirectoryUI>(directoriesOriginalNonDynamic, directoriesUpdatedNonDynamic, NameOf<DirectoryUI>('Id'))
        .map(d => FileStructureTranslator.GetApiDirectoryDto(d));
    const removeNonDynamic: DirectoryDto[] =
      GetUnique<DirectoryUI>(directoriesUpdatedNonDynamic, directoriesOriginalNonDynamic, NameOf<DirectoryUI>('Id'))
        .map(d => FileStructureTranslator.GetApiDirectoryDto(d));

    return new EditJobRequest({
      name: original.Name === updated.Name ? undefined : updated.Name,
      jobScheduleType: scheduleType,
      editSchedule: scheduleEdit,
      options: options,
      runOnceDateTime: scheduleType === JobScheduleType.OnceLater ? updated.StartDate : undefined,
      addFiles: addFiles.length > 0 ? addFiles : undefined,
      removeFiles: removeFiles.length > 0 ? removeFiles : undefined,
      addDirectories: addDirectories.length > 0 ? addDirectories : undefined,
      removeDirectories: removeDirectories.length > 0 ? removeDirectories : undefined,
      addNonDynamicDirectories: addNonDynamic,
      removeNonDynamicDirectories: removeNonDynamic,
      description: original.Description !== updated.Description ? updated.Description : undefined,
      clearDescription: original.Description != null && original.Description.trim() != '' && (updated.Description == null || updated.Description.trim() == '')
    });
  }

  private static GetScheduleFromTask(task: Task): Schedule | undefined {
    switch (task.Trigger) {
      case 'OnceNow':
      case 'OnceLater':
      case 'OnPublish':
        return undefined;
      case 'Recurring':
        const end = task.RecurrenceSettings?.EndType === 'Date' ? task.RecurrenceSettings.EndDate : undefined;
        const runs = task.RecurrenceSettings?.EndType === 'Occurrences' ? task.RecurrenceSettings.MaxRuns : 0;
        return new Schedule({
          cronString: CronTranslator.GetCronString(task.RecurrenceSettings!),
          startDate: task.RecurrenceSettings!.StartDate,
          endDate: end,
          maxRuns: runs
        });
    }
  }

  private static GetSingleFiles(task: Task): { [key: string]: string[] } | undefined {
    if (task.Files == null || task.Files.length === 0) {
      return undefined;
    }

    const value: { [key: string]: string[] } = {};
    for (const entry of task.Files) {
      if (!(entry instanceof ZipTaskSetting)) {
        continue;
      }

      // Skip if the parent is included by itself
      if (!IncludeZip(entry, task)) {
        continue;
      }

      value[entry.File.Id] = entry.Zips.flatMap(z => z.Name);
    }

    return value;
  }

  private static HasUpdatedDirectories(original: Task, updated: Task): boolean {
    if (original.Directories == null) {
      return updated.Directories != null;
    }

    if (updated.Directories == null) {
      // If we got here original is non-null so there are changes
      return true;
    }

    if (original.Directories.length !== updated.Directories.length) {
      return true;
    }

    original.Directories.forEach(d => {
      const found = updated.Directories.find(u => u.Id === d.Id);

      if (
        found == null
        || d.IncludeThisFolder !== found.IncludeThisFolder
        || d.IsDynamic !== found.IsDynamic
        || d.IsRecursive !== found.IsRecursive
        || d.FileTypeFilter?.sort().join() !== found.FileTypeFilter?.sort().join()
      ) {
        return true;
      }
    });

    return false;
  }

  private static HasUpdatedFiles(original: Task, updated: Task): boolean {
    if (original.Files == null) {
      return updated.Files != null;
    }

    if (updated.Files == null) {
      // If we got here original is non-null so there are changes
      return true;
    }

    if (original.Files.length !== updated.Files.length) {
      return true;
    }

    original.Files.forEach(d => {
      if (d instanceof FileUI) {
        const found = updated.Files.find(u => u instanceof FileUI && u.Id === d.Id);

        if (found == null) {
          return true;
        }
      } else {
        const found = updated.Files
          .filter(f => f instanceof ZipTaskSetting && IncludeZip(f, updated))
          .find(u => u instanceof ZipTaskSetting && u.File.Id === d.File.Id) as ZipTaskSetting;

        if (found == null) {
          return true;
        }

        d.Zips.forEach(originalZip => {
          const updatedZip = found.Zips.find(z => z.Name === originalZip.Name);
          if (updatedZip == null) {
            return true;
          }
        });
      }
    });

    return false;
  }
}
